import { type OfferCard, type OpeningHours, type Price, type Slot, type Store } from './store-api';

/**
 * The Store page's pure rules: what each line of the frame says, worked out
 * from the platform's own records and nothing else. Kept apart from the page
 * so every one of them is pinned by a test.
 */

const SHOP_ZONE = 'Europe/London';
const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'] as const;
const SHORT_DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

export function money(minor: number, currency = 'GBP'): string {
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(minor / 100);
}

/** Initials for the shop's plaque: the first letters of its first two words. */
export function initialsOf(name: string): string {
  const words = name
    .replace(/\(.*?\)/g, ' ')
    .split(/[^A-Za-z0-9]+/)
    .filter((word) => word.length > 0 && !/^(and|of|the)$/i.test(word));
  return (
    words
      .slice(0, 2)
      .map((word) => word[0]?.toUpperCase() ?? '')
      .join('') || '?'
  ).slice(0, 2);
}

/** Weekday (0 = Sunday, the platform's numbering) and HH:MM, on the shop's own clock. */
export function shopClock(now: Date): { readonly weekday: number; readonly time: string } {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: SHOP_ZONE,
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(now);
  const get = (type: string): string => parts.find((part) => part.type === type)?.value ?? '';
  const weekday = SHORT_DAYS.indexOf(get('weekday') as (typeof SHORT_DAYS)[number]);
  return { weekday: weekday < 0 ? now.getUTCDay() : weekday, time: `${get('hour')}:${get('minute')}` };
}

export interface OpenStatus {
  readonly open: boolean;
  readonly label: string;
}

/**
 * The hero's status pill, from the week the shop published.
 *
 * Computed here, never sent by the server: whether a shop is open depends on
 * the clock, and a shop that has not published its hours gets no pill at all
 * rather than an "Open now" nobody stated.
 */
export function openStatus(hours: readonly OpeningHours[] | undefined, now: Date): OpenStatus | undefined {
  if (hours === undefined || hours.length === 0) return undefined;
  const { weekday, time } = shopClock(now);
  const today = hours.filter((h) => h.weekday === weekday).sort((a, b) => a.opens.localeCompare(b.opens));

  const current = today.find((h) => h.opens <= time && time < h.closes);
  if (current !== undefined) return { open: true, label: `Open now · until ${current.closes}` };

  const later = today.find((h) => time < h.opens);
  if (later !== undefined) return { open: false, label: `Opens ${later.opens}` };

  for (let ahead = 1; ahead <= 7; ahead += 1) {
    const day = (weekday + ahead) % 7;
    const first = hours.filter((h) => h.weekday === day).sort((a, b) => a.opens.localeCompare(b.opens))[0];
    if (first !== undefined) {
      return {
        open: false,
        label: ahead === 1 ? `Opens tomorrow ${first.opens}` : `Opens ${SHORT_DAYS[day]} ${first.opens}`,
      };
    }
  }
  return { open: false, label: 'Closed' };
}

/** The OPENING HOURS card: Monday first, as the frame lists it, today marked. */
export function hoursTable(
  hours: readonly OpeningHours[],
  now: Date,
): readonly { readonly day: string; readonly times: string; readonly today: boolean }[] {
  const { weekday } = shopClock(now);
  return [1, 2, 3, 4, 5, 6, 0].map((day) => {
    const spans = hours.filter((h) => h.weekday === day).sort((a, b) => a.opens.localeCompare(b.opens));
    return {
      day: `${DAY_NAMES[day] ?? ''}${day === weekday ? ' (today)' : ''}`,
      times: spans.length === 0 ? 'Closed' : spans.map((h) => `${h.opens}–${h.closes}`).join(', '),
      today: day === weekday,
    };
  });
}

/**
 * The fees line under the facts: "No min. order · Fees: Delivery from £2.50".
 *
 * Only the two terms a storefront holds. The frame's service fee and
 * small-order fee are not charged by this platform (owner decision D6), so
 * they are never printed. An absent delivery fee prints nothing: it means the
 * shop has not said, and "Free" would invent a promise.
 */
/**
 * Whether the shop delivers, in the storefront's own vocabulary.
 *
 * A storefront publishes LOCAL_DELIVERY and/or NATIONAL_DELIVERY — never a bare
 * "DELIVERY", which is the checkout's word for the customer's choice. Checking
 * for "DELIVERY" alone read every real shop as collection-only (stage 6).
 */
export function offersDelivery(store: Pick<Store, 'fulfilmentModes'>): boolean {
  return store.fulfilmentModes.some(
    (mode) => mode === 'LOCAL_DELIVERY' || mode === 'NATIONAL_DELIVERY' || mode === 'DELIVERY',
  );
}

export function termsOf(store: Store): { readonly minimum: string; readonly delivery?: string } {
  const currency = store.deliveryCurrency ?? 'GBP';
  const minimum =
    store.minimumOrderMinor === undefined || store.minimumOrderMinor === 0
      ? 'No min. order'
      : `${money(store.minimumOrderMinor, currency)} min. order`;
  if (!offersDelivery(store) || store.deliveryFromMinor === undefined) return { minimum };
  return {
    minimum,
    delivery:
      store.deliveryFromMinor === 0
        ? 'Free delivery'
        : `Delivery from ${money(store.deliveryFromMinor, currency)}`,
  };
}

/** "SAVE 20%" — only from a saving the platform priced, never from a guess. */
export function savingBadge(price: Price): string | undefined {
  if (!price.resolved || price.amountMinor === undefined) return undefined;
  const saving =
    price.savingMinor ??
    (price.compareAtMinor !== undefined && price.compareAtMinor > price.amountMinor
      ? price.compareAtMinor - price.amountMinor
      : undefined);
  if (saving === undefined || saving <= 0) return undefined;
  const percent = Math.round((saving / (price.amountMinor + saving)) * 100);
  return percent >= 1 ? `SAVE ${String(percent)}%` : undefined;
}

export function priceLabel(price: Price): string {
  if (!price.resolved || price.amountMinor === undefined) return 'Price at the pharmacy';
  if (price.amountMinor === 0) return 'Free';
  return money(price.amountMinor, price.currency ?? 'GBP');
}

/** How a medicine may be bought: straight into the basket, after a pharmacist's questions, or a prescriber's. */
export type Legal = 'GSL' | 'CHECK' | 'POM';

export function legalOf(offer: Pick<OfferCard, 'restriction'>): Legal {
  if (offer.restriction.prescriptionRequired) return 'POM';
  if (offer.restriction.consultationRequired) return 'CHECK';
  return 'GSL';
}

export function eyebrowOf(offer: OfferCard, shelf: string | undefined): string {
  const legal = legalOf(offer);
  if (legal === 'POM') return 'PRESCRIPTION ONLY';
  if (legal === 'CHECK') return 'PHARMACIST CHECK';
  return (shelf ?? offer.brandName ?? '').toUpperCase();
}

/** The catalogue's unit codes, as a pack is written on a shelf. */
const MEASURES: Readonly<Record<string, string>> = {
  MICROGRAM: 'mcg',
  MILLIGRAM: 'mg',
  GRAM: 'g',
  KILOGRAM: 'kg',
  MICROLITRE: 'mcl',
  MILLILITRE: 'ml',
  LITRE: 'L',
  MILLIMETRE: 'mm',
  CENTIMETRE: 'cm',
  METRE: 'm',
};
const COUNTS: Readonly<Record<string, string>> = {
  ITEM: 'item',
  TABLET: 'tablet',
  CAPSULE: 'capsule',
  SACHET: 'sachet',
  AMPOULE: 'ampoule',
  PATCH: 'patch',
  DOSE: 'dose',
  APPLICATION: 'application',
  SESSION: 'session',
};

function quantityText(quantity: { readonly amount: number; readonly unit: string }): string {
  const code = quantity.unit.toUpperCase();
  const measure = MEASURES[code];
  if (measure !== undefined) return `${String(quantity.amount)}${measure}`;
  const count = COUNTS[code] ?? quantity.unit.toLowerCase();
  const plural = quantity.amount === 1 ? count : count === 'patch' ? 'patches' : `${count}s`;
  return `${String(quantity.amount)} ${plural}`;
}

/** The size line under a product's name — "16 tablets", "100g" — from the catalogue's own facts. */
export function sizeOf(offer: OfferCard): string | undefined {
  const product = offer.product;
  if (product === undefined) return undefined;
  if (product.packSize !== undefined) return quantityText(product.packSize);
  if (product.netContent !== undefined) return quantityText(product.netContent);
  return undefined;
}

/** "Tablets & capsules" style filter labels, from the catalogue's presentation of each product. */
export function presentationLabel(presentation: string): string {
  const text = presentation.replace(/_/g, ' ').toLowerCase();
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** Miles between two points (haversine). */
export function milesBetween(
  a: { readonly latitude: number; readonly longitude: number },
  b: { readonly latitude: number; readonly longitude: number },
): number {
  const rad = (deg: number): number => (deg * Math.PI) / 180;
  const dLat = rad(b.latitude - a.latitude);
  const dLon = rad(b.longitude - a.longitude);
  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.latitude)) * Math.cos(rad(b.latitude)) * Math.sin(dLon / 2) ** 2;
  return 3958.8 * 2 * Math.asin(Math.sqrt(h));
}

/** "Spend another £9.11 to reach the £15.00 minimum order" — the shop's own minimum, nothing else. */
export function nudgeOf(
  subtotalMinor: number,
  minimumMinor: number | undefined,
  currency = 'GBP',
): { readonly short: string; readonly why: string; readonly percent: number } | undefined {
  if (minimumMinor === undefined || minimumMinor <= 0 || subtotalMinor >= minimumMinor) return undefined;
  return {
    short: money(minimumMinor - subtotalMinor, currency),
    why: `to reach the ${money(minimumMinor, currency)} minimum order`,
    percent: Math.max(4, Math.round((subtotalMinor / minimumMinor) * 100)),
  };
}

// -- Booking -----------------------------------------------------------------

export function dayKey(iso: string, zone: string): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: zone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(iso));
  return parts;
}

export function timeOf(iso: string, zone: string): string {
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: zone,
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(new Date(iso));
}

export function whenLabel(iso: string, zone: string): string {
  const day = new Intl.DateTimeFormat('en-GB', {
    timeZone: zone,
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  }).format(new Date(iso));
  return `${day} · ${timeOf(iso, zone)}`;
}

/** "Today 14:30", "Tomorrow 09:15", "Thu 11:20" — the frame's "Next appointment" fact. */
export function nextLabel(iso: string, now: Date): string {
  const target = dayKey(iso, SHOP_ZONE);
  const today = dayKey(now.toISOString(), SHOP_ZONE);
  const tomorrow = dayKey(new Date(now.getTime() + 86_400_000).toISOString(), SHOP_ZONE);
  const time = timeOf(iso, SHOP_ZONE);
  if (target === today) return `Today ${time}`;
  if (target === tomorrow) return `Tomorrow ${time}`;
  const day = new Intl.DateTimeFormat('en-GB', {
    timeZone: SHOP_ZONE,
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  }).format(new Date(iso));
  return `${day} ${time}`;
}

/** Slots grouped as the frame groups them: MORNING, AFTERNOON, EVENING. */
export function slotGroups(
  slots: readonly Slot[],
): readonly { readonly label: string; readonly slots: readonly Slot[] }[] {
  const groups: { label: string; slots: Slot[] }[] = [
    { label: 'MORNING', slots: [] },
    { label: 'AFTERNOON', slots: [] },
    { label: 'EVENING', slots: [] },
  ];
  for (const slot of slots) {
    const hour = Number(timeOf(slot.startsAt, slot.timeZone).slice(0, 2));
    const group = hour < 12 ? groups[0] : hour < 17 ? groups[1] : groups[2];
    group?.slots.push(slot);
  }
  return groups.filter((group) => group.slots.length > 0);
}

/** A month grid, Monday first, as `YYYY-MM-DD` keys with blanks before the 1st. */
export function monthGrid(year: number, month: number): readonly (string | undefined)[] {
  const first = new Date(Date.UTC(year, month, 1));
  const lead = (first.getUTCDay() + 6) % 7;
  const days = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  const cells: (string | undefined)[] = Array.from({ length: lead }, () => undefined);
  for (let day = 1; day <= days; day += 1) {
    cells.push(`${String(year)}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`);
  }
  return cells;
}

function icsStamp(iso: string): string {
  return new Date(iso)
    .toISOString()
    .replace(/[-:]/g, '')
    .replace(/\.\d{3}/, '');
}

function icsText(value: string): string {
  return value
    .replace(/\\/g, '\\\\')
    .replace(/[,;]/g, (c) => `\\${c}`)
    .replace(/\n/g, '\\n');
}

/** A calendar file built from the booking the platform returned — nothing in it is the page's own. */
export function bookingIcs(input: {
  readonly id: string;
  readonly title: string;
  readonly where: string;
  readonly startsAt: string;
  readonly endsAt: string;
  /** What the event says about the booking. Defaults to the Store's "requested" wording. */
  readonly description?: string;
}): string {
  return [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Just Treated//Store//EN',
    'BEGIN:VEVENT',
    `UID:${input.id}@just-treated.com`,
    `DTSTAMP:${icsStamp(new Date().toISOString())}`,
    `DTSTART:${icsStamp(input.startsAt)}`,
    `DTEND:${icsStamp(input.endsAt)}`,
    `SUMMARY:${icsText(input.title)}`,
    `LOCATION:${icsText(input.where)}`,
    `DESCRIPTION:${icsText(input.description ?? 'Requested through Just Treated. Waiting for the pharmacy to accept.')}`,
    'END:VEVENT',
    'END:VCALENDAR',
    '',
  ].join('\r\n');
}

/** Whether a question is asked, given the answers so far — the platform's own `isShown` rule. */
export function isShown(
  question: {
    readonly id: string;
    readonly showIf?: { readonly questionId: string; readonly anyOf: readonly string[] };
  },
  questions: readonly {
    readonly id: string;
    readonly kind: string;
    readonly showIf?: { readonly questionId: string; readonly anyOf: readonly string[] };
  }[],
  answerFor: (questionId: string) => unknown,
): boolean {
  const condition = question.showIf;
  if (condition === undefined) return true;
  const source = questions.find((candidate) => candidate.id === condition.questionId);
  if (source === undefined || !isShown(source, questions, answerFor)) return false;
  const value = answerFor(source.id);
  if (value === undefined || value === null) return false;
  if (source.kind === 'YES_NO')
    return typeof value === 'boolean' && condition.anyOf.includes(value ? 'YES' : 'NO');
  if (Array.isArray(value)) return value.some((item) => condition.anyOf.includes(String(item)));
  return typeof value === 'string' && condition.anyOf.includes(value);
}
